import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../../../../config/firebaseConfig";
import { login } from "../../../../../redux/features/authSlice";
import { Button, TextInput } from "../../../../../components";
import { validateEmail } from "../../../../../assets/js/validation";
import { toast } from "react-toastify";

const EmailLoginForm = ({
  onBack,
  onSwitchToRegister,
  onUnverified,
  getDashboardTargetUrl,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const resetFieldError = (field) => {
    setError((prev) => (prev ? { ...prev, [field]: null } : null));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const cleanEmail = email.trim().toLowerCase();
    const errors = {};

    if (!cleanEmail) {
      errors.email = "Email is required.";
    } else if (!validateEmail(cleanEmail)) {
      errors.email = "Please enter a valid email address.";
    }

    if (!password) {
      errors.password = "Password is required.";
    }

    if (Object.keys(errors).length > 0) {
      setError(errors);
      return;
    }

    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, cleanEmail, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        if (onUnverified) {
          onUnverified(cleanEmail, password);
        }
        toast.info("Please verify your email address. A verification link was previously sent.");
        return;
      }

      await dispatch(login({ email: cleanEmail, password })).unwrap();
      toast.success("Welcome back!");
      const targetUrl = getDashboardTargetUrl ? getDashboardTargetUrl() : "/dashboard";
      navigate(targetUrl);
    } catch (err) {
      console.error("Email login error:", err);
      const errMsg = typeof err === "string" ? err : err?.message || "Failed to sign in. Please check your credentials.";

      if (
        err?.code === "auth/invalid-credential" ||
        errMsg.toLowerCase().includes("invalid email or password") ||
        errMsg.toLowerCase().includes("invalid-credential")
      ) {
        setError({ other: "Invalid email or password. Please check your credentials and try again." });
      } else if (
        err?.code === "auth/user-not-found" ||
        errMsg.toLowerCase().includes("user not found") ||
        errMsg.toLowerCase().includes("no user found")
      ) {
        setError({ email: "No account found with this email. Please check your email or sign up." });
      } else if (
        err?.code === "auth/wrong-password" ||
        errMsg.toLowerCase().includes("invalid password") ||
        errMsg.toLowerCase().includes("wrong-password")
      ) {
        setError({ password: "Incorrect password. Please try again or reset your password." });
      } else if (
        err?.code === "auth/too-many-requests" ||
        errMsg.toLowerCase().includes("too-many-requests") ||
        errMsg.toLowerCase().includes("temporarily blocked")
      ) {
        setError({
          other:
            "Access to this account has been temporarily disabled due to multiple failed login attempts. Please reset your password or try again later.",
        });
      } else if (err?.code === "auth/user-disabled" || errMsg.toLowerCase().includes("user-disabled")) {
        setError({ other: "This account has been disabled. Please contact support." });
      } else {
        setError({ other: errMsg });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    if (onBack) {
      onBack();
    } else {
      navigate("/login" + (location.search || ""));
    }
  };

  const handleRegisterClick = () => {
    if (onSwitchToRegister) {
      onSwitchToRegister();
    } else {
      navigate("/register/email" + (location.search || ""));
    }
  };

  return (
    <form onSubmit={handleFormSubmit} className="w-full h-full flex flex-col justify-start" noValidate>
      {/* Back Button */}
      <button
        type="button"
        onClick={handleBackClick}
        className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-gray-800 hover:text-black mb-2 transition-colors w-fit focus:outline-none cursor-pointer"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
        </svg>
        <span>Back</span>
      </button>

      {/* Heading & Subheading */}
      <h3 className="text-xl sm:text-2xl font-extrabold text-[#111827] tracking-tight leading-tight mb-0.5">
        Sign In to Practido
      </h3>
      <p className="text-xs text-gray-500 font-medium mb-4">
        Sign in to access your dashboard
      </p>

      {/* Email Input */}
      <div className="form-group flex flex-col mb-3">
        <label htmlFor="email" className="text-xs sm:text-sm font-bold text-gray-700 mb-1">
          Email Address <span className="text-[#E5383B]">*</span>
        </label>
        <TextInput
          type="email"
          placeholderText="alex@example.com"
          inputName="email"
          inputValue={email}
          isRequired={true}
          inputClassName="!h-[44px] !rounded-xl !bg-white !border-gray-200 focus:!border-[#6366F1] !text-sm"
          leftIcon={
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          }
          handleChange={(e) => {
            setEmail(e.target.value);
            resetFieldError("email");
          }}
        />
        {error?.email && <div className="text-xs text-[#f00] mt-1 pl-1">{error.email}</div>}
      </div>

      {/* Password Input */}
      <div className="form-group flex flex-col mb-2">
        <div className="flex items-center justify-between mb-1">
          <label htmlFor="password" className="text-xs sm:text-sm font-bold text-gray-700">
            Password <span className="text-[#E5383B]">*</span>
          </label>
          <Link
            to={`/login/forgotPassword${location.search || ""}`}
            className="text-xs font-bold text-[#009DFF] hover:underline"
          >
            Forgot Password?
          </Link>
        </div>
        <TextInput
          type="password"
          placeholderText="••••••••"
          inputName="password"
          inputValue={password}
          isRequired={true}
          inputClassName="!h-[44px] !rounded-xl !bg-white !border-gray-200 focus:!border-[#6366F1] !text-sm"
          leftIcon={
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
          }
          handleChange={(e) => {
            setPassword(e.target.value);
            resetFieldError("password");
          }}
        />
        {error?.password && <div className="text-xs text-[#f00] mt-1 pl-1">{error.password}</div>}
      </div>

      {error?.other && (
        <div className="text-[#E5383B] text-xs font-semibold mb-2.5">{error.other}</div>
      )}

      {/* Submit Button */}
      <div className="w-full mt-2">
        <Button
          version="secondary"
          text="Sign In"
          children={
            <svg className="w-4 h-4 ml-1 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          }
          type="submit"
          loading={loading}
          width="100%"
          className="w-full justify-center font-extrabold"
        />
      </div>

      {/* Footer Link */}
      <div className="text-center mt-4">
        <p className="text-xs text-gray-500 font-medium">
          Don't have an account?{" "}
          <button
            type="button"
            onClick={handleRegisterClick}
            className="text-[#009DFF] hover:underline font-bold focus:outline-none cursor-pointer"
          >
            Sign up here
          </button>
        </p>
      </div>
    </form>
  );
};

export default EmailLoginForm;
